import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoiceAPI, sellerProfileAPI, bankAccountAPI, customerProfileAPI, itemProfileAPI, noteTemplateAPI } from '../../services/api';
import './InvoiceForm.css';

const InvoiceEditForm = () => {
  const navigate   = useNavigate();
  const { id }     = useParams();

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');

  const [sellerProfiles, setSellerProfiles]           = useState([]);
  const [selectedProfile, setSelectedProfile]         = useState('');
  const [bankAccounts, setBankAccounts]               = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [customerProfiles, setCustomerProfiles]       = useState([]);
  const [selectedCustomerProfile, setSelectedCustomerProfile] = useState('');
  const [itemProfiles, setItemProfiles]               = useState([]);
  const [noteTemplates, setNoteTemplates]             = useState([]);

  const [formData, setFormData] = useState({
    invoice_date:        '',
    bank_account_id:     '',
    seller_name:         '',
    seller_address:      '',
    seller_gstin:        '',
    seller_contact:      '',
    customer_name:       '',
    billing_address:     '',
    shipping_address:    '',
    customer_gstin:      '',
    state_name:          '',
    state_code:          '',
    note:                '',
    service_period_from: '',
    service_period_to:   '',
  });

  const [items, setItems] = useState([]);

  useEffect(() => {
    // Load all supporting data in parallel, then load invoice
    Promise.all([
      sellerProfileAPI.getAll().then(r => r.success ? setSellerProfiles(r.data) : null),
      bankAccountAPI.getAll().then(r => r.success ? setBankAccounts(r.data) : null),
      customerProfileAPI.getAll().then(r => r.success ? setCustomerProfiles(r.data) : null),
      itemProfileAPI.getAll().then(r => r.success ? setItemProfiles(r.data) : null),
      noteTemplateAPI.getAll().then(r => r.success ? setNoteTemplates(r.data) : null),
    ]).then(() => loadInvoice()).catch(() => loadInvoice());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadInvoice = async () => {
    try {
      const res = await invoiceAPI.getById(id);
      if (!res.success) { setError('Invoice not found'); setLoading(false); return; }
      const inv = res.data;

      setInvoiceNumber(inv.invoice_number);
      setSelectedBankAccount(inv.bank_account_id ? inv.bank_account_id.toString() : '');

      setFormData({
        invoice_date:        inv.invoice_date?.split('T')[0] ?? inv.invoice_date ?? '',
        bank_account_id:     inv.bank_account_id ?? '',
        seller_name:         inv.seller_name ?? '',
        seller_address:      inv.seller_address ?? '',
        seller_gstin:        inv.seller_gstin ?? '',
        seller_contact:      inv.seller_contact ?? '',
        customer_name:       inv.customer_name ?? '',
        billing_address:     inv.billing_address ?? '',
        shipping_address:    inv.shipping_address ?? '',
        customer_gstin:      inv.customer_gstin ?? '',
        state_name:          inv.state_name ?? '',
        state_code:          inv.state_code ?? '',
        note:                inv.note ?? '',
        service_period_from: inv.service_period_from?.split('T')[0] ?? inv.service_period_from ?? '',
        service_period_to:   inv.service_period_to?.split('T')[0] ?? inv.service_period_to ?? '',
      });

      setItems((inv.items ?? []).map(item => ({
        description:  item.description,
        hsn_sac_code: item.hsn_sac_code,
        quantity:     parseFloat(item.quantity),
        unit:         item.unit || 'Nos',
        unit_price:   parseFloat(item.unit_price),
        discount:     parseFloat(item.discount || 0),
        gst_rate:     parseInt(item.gst_rate, 10),
        tax_type:     item.tax_type || 'exclusive',
      })));
    } catch {
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSelect = (profileId) => {
    setSelectedProfile(profileId);
    if (profileId) {
      const p = sellerProfiles.find(p => p.id.toString() === profileId);
      if (p) setFormData(prev => ({ ...prev, seller_name: p.seller_name, seller_address: p.seller_address, seller_gstin: p.seller_gstin || '', seller_contact: p.seller_contact || '' }));
    }
  };

  const handleBankAccountSelect = (accountId) => {
    setSelectedBankAccount(accountId);
    setFormData(prev => ({ ...prev, bank_account_id: accountId || '' }));
  };

  const handleCustomerProfileSelect = (profileId) => {
    setSelectedCustomerProfile(profileId);
    if (profileId) {
      const p = customerProfiles.find(p => p.id.toString() === profileId);
      if (p) setFormData(prev => ({ ...prev, customer_name: p.customer_name, billing_address: p.billing_address, shipping_address: p.shipping_address || '', customer_gstin: p.customer_gstin || '', state_name: p.state_name || '', state_code: p.state_code || '' }));
    }
  };

  const handleItemProfileSelect = (index, profileId) => {
    if (!profileId) return;
    const profile = itemProfiles.find(p => p.id.toString() === profileId);
    if (!profile) return;
    const newItems = [...items];
    newItems[index] = { ...newItems[index], description: profile.description, hsn_sac_code: profile.hsn_sac_code || '', unit: profile.unit, unit_price: profile.unit_price, gst_rate: parseInt(profile.gst_rate, 10), tax_type: profile.tax_type || 'exclusive' };
    setItems(newItems);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems(prev => [...prev, { description: '', hsn_sac_code: '', quantity: 1, unit: 'Nos', unit_price: 0, discount: 0, gst_rate: 18, tax_type: 'exclusive' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) setItems(items.filter((_, i) => i !== index));
  };

  const calculateItemTotal = (item) => {
    const base = item.quantity * item.unit_price - (item.discount || 0);
    if (item.tax_type === 'inclusive') return base;
    return base + (base * item.gst_rate) / 100;
  };

  const calculateGrandTotal = () => items.reduce((t, item) => t + calculateItemTotal(item), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const invoiceData = {
        ...formData,
        items: items.map(item => ({
          ...item,
          quantity:   parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          discount:   parseFloat(item.discount || 0),
          gst_rate:   parseFloat(item.gst_rate),
        })),
      };
      const response = await invoiceAPI.update(id, invoiceData);
      if (response.success) {
        alert('Invoice updated successfully!');
        navigate('/dashboard/invoices');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading invoice...</div>;

  return (
    <div className="invoice-form-container">
      <div className="form-header">
        <h2>Edit Invoice {invoiceNumber}</h2>
        <button type="button" onClick={() => navigate('/dashboard/invoices')} className="back-btn">
          Back to List
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Invoice Details */}
        <div className="form-section">
          <h3>Invoice Details</h3>
          <div className="form-row">
            <div className="form-field">
              <label>Invoice Number</label>
              <input type="text" value={invoiceNumber} readOnly style={{ background: '#f3f4f6', color: '#374151', cursor: 'default', fontWeight: 600 }} />
            </div>
            <div className="form-field">
              <label>Invoice Date *</label>
              <input type="date" name="invoice_date" value={formData.invoice_date} onChange={handleInputChange} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label>Service Period From</label>
              <input type="date" name="service_period_from" value={formData.service_period_from} onChange={handleInputChange} />
            </div>
            <div className="form-field">
              <label>Service Period To</label>
              <input type="date" name="service_period_to" value={formData.service_period_to} onChange={handleInputChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field full-width">
              <div className="section-header-with-action" style={{ marginBottom: 6 }}>
                <label style={{ margin: 0 }}>Note</label>
                <button type="button" onClick={() => navigate('/dashboard/invoice-settings?tab=note-templates')} className="manage-profiles-btn">Manage Notes</button>
              </div>
              {noteTemplates.length > 0 && (
                <select onChange={(e) => { if (!e.target.value) return; const t = noteTemplates.find(n => n.id.toString() === e.target.value); if (t) setFormData(prev => ({ ...prev, note: t.note })); e.target.value = ''; }} className="profile-selector" style={{ marginBottom: 6 }} defaultValue="">
                  <option value="">-- Select a saved note --</option>
                  {noteTemplates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              )}
              <textarea name="note" value={formData.note} onChange={handleInputChange} rows="3" placeholder="Add any additional notes for this invoice" />
            </div>
          </div>
        </div>

        {/* Seller Details */}
        <div className="form-section">
          <div className="section-header-with-action">
            <h3>Seller Details</h3>
            <button type="button" onClick={() => navigate('/dashboard/invoice-settings?tab=seller-profiles')} className="manage-profiles-btn">Manage Profiles</button>
          </div>
          <div className="form-row">
            <div className="form-field full-width">
              <label>Select Seller Profile</label>
              <select value={selectedProfile} onChange={e => handleProfileSelect(e.target.value)} className="profile-selector">
                <option value="">-- Select a profile or enter manually --</option>
                {sellerProfiles.map(p => <option key={p.id} value={p.id}>{p.profile_name} - {p.seller_name}{p.is_default && ' (Default)'}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-field"><label>Seller Name *</label><input type="text" name="seller_name" value={formData.seller_name} onChange={handleInputChange} required /></div>
            <div className="form-field"><label>GSTIN</label><input type="text" name="seller_gstin" value={formData.seller_gstin} onChange={handleInputChange} /></div>
          </div>
          <div className="form-row">
            <div className="form-field full-width"><label>Address *</label><textarea name="seller_address" value={formData.seller_address} onChange={handleInputChange} required rows="2" /></div>
          </div>
          <div className="form-row">
            <div className="form-field"><label>Contact</label><input type="text" name="seller_contact" value={formData.seller_contact} onChange={handleInputChange} /></div>
          </div>
        </div>

        {/* Bank Account */}
        <div className="form-section">
          <div className="section-header-with-action">
            <h3>Bank Account Details</h3>
            <button type="button" onClick={() => navigate('/dashboard/invoice-settings?tab=bank-accounts')} className="manage-profiles-btn">Manage Accounts</button>
          </div>
          <div className="form-row">
            <div className="form-field full-width">
              <label>Select Bank Account (Optional)</label>
              <select value={selectedBankAccount} onChange={e => handleBankAccountSelect(e.target.value)} className="profile-selector">
                <option value="">-- Select a bank account --</option>
                {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name} - {a.account_number}{a.is_default && ' (Default)'}</option>)}
              </select>
              <small style={{ color: '#7f8c8d', marginTop: '5px', display: 'block' }}>Bank details will appear on the invoice if selected</small>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="form-section">
          <div className="section-header-with-action">
            <h3>Customer Details</h3>
            <button type="button" onClick={() => navigate('/dashboard/invoice-settings?tab=customer-profiles')} className="manage-profiles-btn">Manage Profiles</button>
          </div>
          <div className="form-row">
            <div className="form-field full-width">
              <label>Select Customer Profile</label>
              <select value={selectedCustomerProfile} onChange={e => handleCustomerProfileSelect(e.target.value)} className="profile-selector">
                <option value="">-- Select a profile or enter manually --</option>
                {customerProfiles.map(p => <option key={p.id} value={p.id}>{p.profile_name} - {p.customer_name}{p.is_default && ' (Default)'}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-field"><label>Customer Name *</label><input type="text" name="customer_name" value={formData.customer_name} onChange={handleInputChange} required /></div>
            <div className="form-field"><label>Customer GSTIN</label><input type="text" name="customer_gstin" value={formData.customer_gstin} onChange={handleInputChange} /></div>
          </div>
          <div className="form-row">
            <div className="form-field full-width"><label>Billing Address *</label><textarea name="billing_address" value={formData.billing_address} onChange={handleInputChange} required rows="2" /></div>
          </div>
          <div className="form-row">
            <div className="form-field full-width"><label>Shipping Address</label><textarea name="shipping_address" value={formData.shipping_address} onChange={handleInputChange} rows="2" /></div>
          </div>
          <div className="form-row">
            <div className="form-field"><label>State Name *</label><input type="text" name="state_name" value={formData.state_name} onChange={handleInputChange} required /></div>
            <div className="form-field"><label>State Code *</label><input type="text" name="state_code" value={formData.state_code} onChange={handleInputChange} required placeholder="e.g., 27 for Maharashtra" /></div>
          </div>
        </div>

        {/* Items */}
        <div className="form-section">
          <div className="section-header-with-action">
            <h3>Invoice Items</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={() => navigate('/dashboard/invoice-settings?tab=item-profiles')} className="manage-profiles-btn">Manage Items</button>
              <button type="button" onClick={addItem} className="add-item-btn">+ Add Item</button>
            </div>
          </div>

          {items.map((item, index) => (
            <div key={index} className="item-card">
              <div className="item-header">
                <h4>Item {index + 1}</h4>
                {items.length > 1 && <button type="button" onClick={() => removeItem(index)} className="remove-item-btn">Remove</button>}
              </div>

              {itemProfiles.length > 0 && (
                <div className="form-row">
                  <div className="form-field full-width">
                    <label>Select Saved Item (optional)</label>
                    <select onChange={e => handleItemProfileSelect(index, e.target.value)} className="profile-selector" defaultValue="">
                      <option value="">-- Select a saved item to auto-fill --</option>
                      {itemProfiles.map(p => <option key={p.id} value={p.id}>{p.profile_name} — {p.description}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-field full-width"><label>Description *</label><input type="text" value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} required /></div>
              </div>
              <div className="form-row">
                <div className="form-field"><label>HSN/SAC Code *</label><input type="text" value={item.hsn_sac_code} onChange={e => handleItemChange(index, 'hsn_sac_code', e.target.value)} required /></div>
                <div className="form-field"><label>Quantity *</label><input type="number" step="0.01" value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} required /></div>
                <div className="form-field"><label>Unit</label><input type="text" value={item.unit} onChange={e => handleItemChange(index, 'unit', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-field"><label>Unit Price *</label><input type="number" step="0.01" value={item.unit_price} onChange={e => handleItemChange(index, 'unit_price', e.target.value)} required /></div>
                <div className="form-field"><label>Discount</label><input type="number" step="0.01" value={item.discount} onChange={e => handleItemChange(index, 'discount', e.target.value)} /></div>
                <div className="form-field">
                  <label>GST Rate (%) *</label>
                  <select value={item.gst_rate} onChange={e => handleItemChange(index, 'gst_rate', e.target.value)} required>
                    <option value="0">0%</option><option value="5">5%</option><option value="12">12%</option><option value="18">18%</option><option value="28">28%</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Tax Amount</label>
                  <input type="text" readOnly value={(() => {
                    const base = item.quantity * item.unit_price - (item.discount || 0);
                    const rate = parseFloat(item.gst_rate) || 0;
                    const tax  = item.tax_type === 'inclusive' ? base * rate / (100 + rate) : base * rate / 100;
                    return `₹${tax.toFixed(2)} (${item.tax_type === 'inclusive' ? 'Incl.' : 'Excl.'})`;
                  })()} style={{ background: '#f9fafb', color: '#374151', cursor: 'default' }} />
                </div>
              </div>
              <div className="item-total">Item Total: ₹{calculateItemTotal(item).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="total-section">
          <h3>Grand Total: ₹{calculateGrandTotal().toFixed(2)}</h3>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/dashboard/invoices')} className="cancel-btn">Cancel</button>
          <button type="submit" disabled={saving} className="submit-btn">{saving ? 'Saving...' : 'Update Invoice'}</button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceEditForm;
